import React, { useState } from "react";
import { easasConfirmDialog } from "components/fragments/easas-global-fragment/EasasGlobalConfirmDialog";
import { DashboardComponentGroup } from "api/model/dashboard/DashboardComponentGroup";
import { DashboardLayoutType } from "api/model/dashboard/DashboardComponent";
import { Button } from "primereact/button";
import useDashboardStore from "store/dashboard-store";
import { I, T } from "services/lang/LanguageService";
import { Dialog } from "primereact/dialog";
import { InputText } from "primereact/inputtext";
import DashboardComponentConfiguratorNew, {
  ConfiguratorModes,
  defaultComponent,
} from "../group-component/GroupComponentConfiguratorNew";
import GroupConfigurator from "./GroupConfigurator";
import VerticalLayout from "../layouts/vertical/VerticalLayout";
import HorizontalFlexLayout from "../layouts/horizontal-flex/HorizontalFlexLayout";
import "../../dashboard-styles/DashboardTabGroup.scss";
import { tabUpdateInDashboard, updateDashboardTabs } from "../../utils";
import { ReorderType } from "./reorder/DraggableItem";
import Reorder from "./reorder/Reorder";

import MoveGroupOrComponent from "../group-component/MoveGroupOrComponent";

interface DashboardTabGroupProps {
  group: DashboardComponentGroup;
  tabId: number;
}

const DashboardTabGroup = ({ group, tabId }: DashboardTabGroupProps) => {
  const dashboard = useDashboardStore(state => state.dashboard);
  const setIsGroupReorderEnabled = useDashboardStore(state => state.setIsGroupReorderEnabled);
  const tabContextConfig = useDashboardStore(state => state.getTabContextConfig(tabId));
  const isEditMode = useDashboardStore(state => state.isEditMode);
  const isGroupReorderEnabled = useDashboardStore(state => state.isGroupReorderEnabled);
  const [isMenuVisible, setIsMenuVisible] = useState<boolean>(false);
  const [groupConfiguratorVisibility, setGroupConfiguratorVisibility] = useState<boolean>(false);
  const [groupTitleChangeDialogVisibility, setGroupTitleChangeDialogVisibility] =
    useState<boolean>(false);
  const [moveTabGroupDialogVisibility, setMoveTabGroupDialogVisibility] = useState<boolean>(false);
  const [
    dashboardComponentCreationDialogVisibility,
    setDashboardComponentCreationDialogVisibility,
  ] = useState<boolean>(false);
  const [newGroupTitle, setNewGroupTitle] = useState<string>(group.title || "");
  const [isComponentsReorderEnabled, setIsComponentsReorderEnabled] = useState<boolean>(false);

  const handleReorderComponents = () => {
    setIsGroupReorderEnabled(false);
    setIsComponentsReorderEnabled(!isComponentsReorderEnabled);
  };

  const groupRemoveConfirm = (id: string) => {
    easasConfirmDialog({
      icon: "pi pi-exclamation-triangle",
      header: T("confirm-dialog.remove-group"),
      message: T("confirm-dialog.are-you-sure-you-want-to-delete-this-group"),
      confirmedCallback: () => {
        handleRemoveGroup(id);
      },
    });
  };

  const handleRemoveGroup = (id: string) => {
    if (!dashboard) return;

    const currentTabIndex = tabId;
    const updatedComponentGroups = dashboard.dashboardTabsJs[
      currentTabIndex
    ].componentGroups.filter((groupToFilter: DashboardComponentGroup) => groupToFilter.id !== id);

    const updatedDashboardTabs = tabUpdateInDashboard(dashboard.dashboardTabsJs, currentTabIndex, {
      componentGroups: updatedComponentGroups,
    });

    updateDashboardTabs(dashboard, updatedDashboardTabs);
  };

  const getLayoutComponent = () => {
    switch (group.layout.type) {
      case DashboardLayoutType.VERTICAL:
        return VerticalLayout;
      case DashboardLayoutType.HORIZONTAL_FLEX:
      default:
        return HorizontalFlexLayout;
    }
  };

  const LayoutComponent = getLayoutComponent();

  const handleSaveGroupTitle = () => {
    if (!dashboard) return;

    updateDashboardTabs(
      dashboard,
      dashboard?.dashboardTabsJs.map((tab, index) => {
        if (index === tabId) {
          return {
            ...tab,
            componentGroups: tab.componentGroups.map(g =>
              g.id === group.id ? { ...g, title: newGroupTitle } : g
            ),
          };
        }
        return tab;
      })
    );

    setGroupTitleChangeDialogVisibility(false);
  };

  const titleChangeFooter = () => (
    <div>
      <Button
        icon="pi pi-times"
        className="p-button-rounded p-button-text"
        onClick={() => setGroupTitleChangeDialogVisibility(false)}
      />
      <Button
        icon="pi pi-check"
        className="p-button-rounded p-button-text"
        onClick={handleSaveGroupTitle}
      />
    </div>
  );

  return (
    <div
      className={`w-full position-relative border-round mt-2 ${
        isEditMode
          ? "border-500 border-2 pr-3"
          : `${tabContextConfig?.showBorders ? "border-1 border-400" : ""}`
      }`}
      style={{ position: "relative", minHeight: "10rem" }}
    >
      <h4 className="mt-2 ml-2">{I(group.title || "")}</h4>
      {isEditMode && (
        <>
          {isGroupReorderEnabled ? (
            <Button
              icon="pi pi-arrows-alt"
              className="p-button-secondary p-button-rounded p-button-text group-main-editor-button bg-white"
              disabled
            />
          ) : (
            <Button
              icon="pi pi-pencil"
              className="p-button-secondary p-button-rounded p-button-text group-main-editor-button bg-white"
              onClick={() => setIsMenuVisible(!isMenuVisible)}
            />
          )}
          {isMenuVisible && (
            <div className="menu-styles">
              <Button
                icon="pi pi-trash"
                tooltip={T("confirm-dialog.remove-group")}
                className="p-button-danger p-button-rounded"
                onClick={() => groupRemoveConfirm(group.id)}
                tooltipOptions={{ position: "bottom" }}
              />
              <Button
                icon="pi pi-cog"
                tooltip={T("action.configure-group")}
                className="p-button-warning p-button-rounded"
                onClick={() => setGroupConfiguratorVisibility(true)}
                tooltipOptions={{ position: "bottom" }}
              />
              <Button
                icon="pi pi-arrow-right"
                tooltip={T("action.move-group")}
                className="p-button-secondary p-button-rounded"
                onClick={() => setMoveTabGroupDialogVisibility(true)}
                tooltipOptions={{ position: "bottom" }}
              />
              <Button
                icon="pi pi-arrow-right-arrow-left"
                tooltip={T("action.reorder-components")}
                className={`${isComponentsReorderEnabled ? "p-button-warning p-button-rounded rotate-90 border" : "p-button-secondary p-button-rounded rotate-90"}`}
                onClick={handleReorderComponents}
                tooltipOptions={{ position: "bottom" }}
              />
              <Button
                icon="pi pi-pencil"
                tooltip={T("action.change-group-title")}
                className="p-button-success p-button-rounded"
                onClick={() => setGroupTitleChangeDialogVisibility(true)}
                tooltipOptions={{ position: "bottom" }}
              />

              <Button
                icon="pi pi-plus"
                tooltip={T("action.add-component")}
                className="p-button-success p-button-rounded"
                onClick={() => setDashboardComponentCreationDialogVisibility(true)}
                tooltipOptions={{ position: "bottom" }}
              />
            </div>
          )}
        </>
      )}
      <LayoutComponent
        components={group.components}
        layoutType={group.layout.type}
        groupId={group.id}
        align={group.layout.align}
        justify={group.layout.justify}
      />
      {isComponentsReorderEnabled && (
        <Reorder
          setReorderDialogVisibility={setIsComponentsReorderEnabled}
          reorderDialogVisibility={isComponentsReorderEnabled}
          reorderType={ReorderType.COMPONENTS}
          group={group}
          tabId={tabId}
        />
      )}
      {dashboardComponentCreationDialogVisibility && isEditMode && (
        <DashboardComponentConfiguratorNew
          isDialogVisible={dashboardComponentCreationDialogVisibility}
          mode={ConfiguratorModes.ADD}
          setIsDialogVisible={setDashboardComponentCreationDialogVisibility}
          component={defaultComponent(group.layout.type)}
          layoutType={group.layout.type}
          groupId={group.id}
        />
      )}
      {groupConfiguratorVisibility && isEditMode && (
        <GroupConfigurator
          group={group}
          isDialogVisible={groupConfiguratorVisibility}
          setIsDialogVisible={setGroupConfiguratorVisibility}
        />
      )}
      {groupTitleChangeDialogVisibility && (
        <Dialog
          header={T("action.change-group-title")}
          style={{ width: "20rem", height: "16rem" }}
          visible={groupTitleChangeDialogVisibility}
          onHide={() => setGroupTitleChangeDialogVisibility(false)}
          footer={titleChangeFooter}
        >
          <div>
            <div>{group.title}</div>
            <InputText
              value={newGroupTitle}
              className="w-full"
              onChange={e => setNewGroupTitle(e.target.value)}
            />
          </div>
        </Dialog>
      )}
      {moveTabGroupDialogVisibility && dashboard && (
        <MoveGroupOrComponent
          dashboardId={dashboard?.id}
          isVisible={moveTabGroupDialogVisibility}
          setIsVisible={setMoveTabGroupDialogVisibility}
          groupId={group.id}
          group={group}
          tabId={tabId}
          isComponent={false}
        />
      )}
    </div>
  );
};

export default DashboardTabGroup;
